import React, { useEffect, useState } from "react";
import { api, resolveImageSrc } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// --- Pending credit claims ---
export function AdminClaims() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null); // claim id being decided

  const load = () => {
    const url = filter === "all" ? "/admin/claims" : `/admin/claims?status=${filter}`;
    api.get(url).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(load, [filter]);

  const decide = async (c, approve) => {
    let pts = null;
    let reason = "";
    if (approve) {
      const suggested = Math.max(1, Math.floor((c.amount || 0) / 100));
      const input = window.prompt(`Approve ${c.user_name}'s ₹${c.amount} ${c.partner} claim. HealthCoin to award (rule: ₹100 = 1 coin):`, String(suggested));
      if (input === null) return;
      const parsed = parseInt(input, 10);
      if (!parsed || parsed <= 0) { toast.error("Enter a positive number"); return; }
      pts = parsed;
    } else {
      reason = window.prompt("Reason for rejection (optional):", "Could not verify") || "Could not verify";
    }
    setBusy(c.id);
    try {
      await api.post(`/admin/claims/${c.id}/decide`, { approve, points: pts, reason });
      toast.success(approve ? `+${pts} credits awarded` : "Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"}
            data-testid={`admin-claims-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`rounded-xl capitalize ${filter === s ? "bg-[#0F3D2E] hover:bg-[#0F3D2E]/90" : ""}`}>
            {s}
          </Button>
        ))}
        <div className="ml-auto text-xs text-slate-500">{items.length} item{items.length === 1 ? "" : "s"}</div>
      </div>
      {items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No claims here.</div>}
      <div className="space-y-2" data-testid="admin-claims-list">
        {items.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-slate-100" data-testid={`admin-claim-${c.id}`}>
            <a href={c.receipt_url ? resolveImageSrc(c.receipt_url) : undefined} target="_blank" rel="noopener noreferrer"
              className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {c.receipt_url
                ? <img src={resolveImageSrc(c.receipt_url)} alt="" className="w-full h-full object-cover" />
                : <span className="text-slate-400">📄</span>}
            </a>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{c.user_name} <span className="text-slate-400 text-sm">· {c.user_phone || "no phone"}</span></div>
              <div className="text-sm text-slate-600">{c.partner} · <strong>₹{c.amount}</strong></div>
              {c.notes && <div className="text-xs text-slate-500 mt-0.5">"{c.notes}"</div>}
              <div className="text-[10px] text-slate-400 mt-1">
                {new Date(c.created_at).toLocaleString()} · <span className="capitalize">{c.status}</span>
                {c.points_awarded ? ` · +${c.points_awarded} pts` : ""}
              </div>
            </div>
            {c.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-xl bg-[#0F3D2E] hover:bg-[#0F3D2E]/90" disabled={busy === c.id}
                  data-testid={`admin-claim-approve-${c.id}`}
                  onClick={() => decide(c, true)}>Approve</Button>
                <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-100 hover:bg-red-50" disabled={busy === c.id}
                  data-testid={`admin-claim-reject-${c.id}`}
                  onClick={() => decide(c, false)}>Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

// --- All users summary ---
export function AdminUsersSummary() {
  const [rows, setRows] = useState([]);
  const [shops, setShops] = useState([]);
  const [walletFor, setWalletFor] = useState(null); // user row being funded
  const load = () => api.get("/admin/users-summary").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/shops").then((r) => setShops((Array.isArray(r.data) ? r.data : r.data?.shops) || [])).catch(() => setShops([])); }, []);
  return (
    <Card className="rounded-2xl border-slate-100"><CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-summary-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Phone</th>
            <th className="p-3 text-right">HealthCoins</th>
            <th className="p-3 text-right">Wallet ₹</th>
            <th className="p-3 text-right">Family</th>
            <th className="p-3 text-right">Wallet</th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3 font-medium text-slate-900">{u.name || "—"}</td>
                <td className="p-3 text-slate-600">{u.phone || u.email || "—"}</td>
                <td className="p-3 text-right font-semibold text-[#0F3D2E]">🌱 {u.balance}</td>
                <td className="p-3 text-right font-semibold text-[#1453A6]" data-testid={`admin-user-cash-${u.id}`}>₹{u.cash_balance ?? 0}</td>
                <td className="p-3 text-right">{u.family_count}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" className="rounded-full text-[#1453A6] border-blue-200" data-testid={`admin-add-wallet-${u.id}`} onClick={() => setWalletFor(u)}>+ Add cash</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {walletFor && (
        <WalletAddModal user={walletFor} shops={shops} onClose={() => setWalletFor(null)} onDone={() => { setWalletFor(null); load(); }} />
      )}
    </CardContent></Card>
  );
}

function WalletAddModal({ user, shops, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const filtered = shops.filter((s) => (s.name || "").toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    if (selected.length === 0) { toast.error("Select at least one shop"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/wallet`, { amount: amt, shop_ids: selected, note: note.trim() || undefined });
      toast.success(`₹${amt} added to ${user.name || "user"}'s wallet`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add cash");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-wallet-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.5)", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 16, maxWidth: 440, width: "100%", maxHeight: "88vh", overflowY: "auto", padding: 20 }}>
        <div className="font-semibold text-slate-900 text-lg">Add wallet cash</div>
        <div className="text-xs text-slate-500 mb-3">for <b>{user.name || user.email}</b> · current ₹{user.cash_balance ?? 0}</div>
        <label className="text-[11px] font-semibold uppercase text-slate-500">Amount (₹)</label>
        <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 500" className="rounded-lg mt-1 mb-3" data-testid="admin-wallet-amount" />
        <label className="text-[11px] font-semibold uppercase text-slate-500">Usable at these shops</label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shops…" className="rounded-lg mt-1 mb-2" data-testid="admin-wallet-shop-search" />
        <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto" data-testid="admin-wallet-shop-list">
          {filtered.length === 0 && <div className="text-xs text-slate-400 p-3">No shops</div>}
          {filtered.map((s) => (
            <label key={s.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-50 cursor-pointer text-sm" data-testid={`admin-wallet-shop-${s.id}`}>
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
              <span className="truncate">{s.name}</span>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 mt-1 mb-3">{selected.length} shop{selected.length === 1 ? "" : "s"} selected</div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="rounded-lg mb-3" data-testid="admin-wallet-note" />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="rounded-xl bg-[#0F3D2E] hover:bg-[#0F3D2E]/90" disabled={busy} onClick={submit} data-testid="admin-wallet-submit">{busy ? "Adding…" : "Add cash"}</Button>
        </div>
      </div>
    </div>
  );
}

// --- Home Check bookings ---
export function AdminHomeChecks() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/home-checks").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (id, body) => {
    try {
      await api.patch(`/admin/home-checks/${id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 space-y-3">
      {items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No Home Checks yet.</div>}
      <div className="space-y-3" data-testid="admin-home-checks-list">
        {items.map((b) => <HomeCheckRow key={b.id} b={b} onSave={(body) => save(b.id, body)} />)}
      </div>
    </CardContent></Card>
  );
}

function HomeCheckRow({ b, onSave }) {
  const [status, setStatus] = useState(b.status || "booked");
  const [bp, setBp] = useState(b.readings?.bp || "");
  const [glucose, setGlucose] = useState(b.readings?.glucose || "");
  const [bmi, setBmi] = useState(b.readings?.bmi || "");
  const [spo2, setSpo2] = useState(b.readings?.spo2 || "");
  const [notes, setNotes] = useState(b.notes || "");

  return (
    <div className="p-4 rounded-xl border border-slate-100" data-testid={`admin-home-check-${b.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-medium text-slate-900">{b.family_member_name || "—"}</div>
          <div className="text-xs text-slate-500">{b.phone || "—"} · {b.slot || b.preferred_date} · {b.address}</div>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-home-check-status-${b.id}`}
          className="rounded-lg border border-slate-200 px-2 py-1 text-sm">
          <option value="booked">Booked</option>
          <option value="visited">Visited</option>
          <option value="report_shared">Report shared</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <ReadInput label="BP" v={bp} onChange={setBp} placeholder="120/80" testid={`admin-home-check-bp-${b.id}`} />
        <ReadInput label="Glucose" v={glucose} onChange={setGlucose} placeholder="110 mg/dL" testid={`admin-home-check-glucose-${b.id}`} />
        <ReadInput label="BMI" v={bmi} onChange={setBmi} placeholder="24.5" testid={`admin-home-check-bmi-${b.id}`} />
        <ReadInput label="SpO₂" v={spo2} onChange={setSpo2} placeholder="98%" testid={`admin-home-check-spo2-${b.id}`} />
      </div>
      <Input placeholder="Notes from the visit…" value={notes} onChange={(e) => setNotes(e.target.value)}
        className="rounded-lg text-sm mb-2" data-testid={`admin-home-check-notes-${b.id}`} />
      <div className="flex justify-end">
        <Button size="sm" className="rounded-xl bg-[#0F3D2E] hover:bg-[#0F3D2E]/90"
          data-testid={`admin-home-check-save-${b.id}`}
          onClick={() => onSave({ status, readings_bp: bp, readings_glucose: glucose, readings_bmi: bmi, readings_spo2: spo2, notes })}>
          Save
        </Button>
      </div>
    </div>
  );
}

function ReadInput({ label, v, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <Input placeholder={placeholder} value={v} onChange={(e) => onChange(e.target.value)}
        className="rounded-lg text-sm" data-testid={testid} />
    </div>
  );
}

// --- Lab bookings ---
export function AdminLabBookings() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/lab-bookings").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (id, body) => {
    try {
      await api.patch(`/admin/lab-bookings/${id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 space-y-3">
      {items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No Lab bookings yet.</div>}
      <div className="space-y-3" data-testid="admin-lab-bookings-list">
        {items.map((b) => <LabRow key={b.id} b={b} onSave={(body) => save(b.id, body)} />)}
      </div>
    </CardContent></Card>
  );
}

function LabRow({ b, onSave }) {
  const [status, setStatus] = useState(b.status || "booked");
  const [notes, setNotes] = useState(b.notes || "");
  return (
    <div className="p-4 rounded-xl border border-slate-100" data-testid={`admin-lab-booking-${b.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-medium text-slate-900">{b.family_member_name || "—"}</div>
          <div className="text-xs text-slate-500">{b.phone || "—"} · {b.slot || b.preferred_date} · {b.address}</div>
          <div className="text-xs text-slate-400 mt-1">Credits used: {b.points_applied || 0}</div>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-lab-status-${b.id}`}
          className="rounded-lg border border-slate-200 px-2 py-1 text-sm">
          <option value="booked">Booked</option>
          <option value="lab_contacted">Lab contacted</option>
          <option value="sample_collected">Sample collected</option>
          <option value="report_ready">Report ready</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <Input placeholder="Notes…" value={notes} onChange={(e) => setNotes(e.target.value)}
        className="rounded-lg text-sm mb-2" data-testid={`admin-lab-notes-${b.id}`} />
      <div className="flex justify-end">
        <Button size="sm" className="rounded-xl bg-[#0F3D2E] hover:bg-[#0F3D2E]/90"
          data-testid={`admin-lab-save-${b.id}`}
          onClick={() => onSave({ status, notes })}>
          Save
        </Button>
      </div>
    </div>
  );
}


// --- Image fixes: products waiting for Mapbazar admin to polish/replace the photo ---
export function AdminImageFixes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/products/image-pending")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = (p) => {
    if (items.length === 0) return;
    setItems((prev) => prev.filter((x) => x.id !== p.id));
  };

  return (
    <Card className="rounded-2xl border-slate-100">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Products awaiting digital photo</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Shopkeepers whose AI image generation failed. Upload a clean catalogue photo and edit details if needed.
            </p>
          </div>
          <div className="text-xs text-slate-500" data-testid="admin-image-fixes-count">
            {items.length} item{items.length === 1 ? "" : "s"}
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="admin-image-fixes-empty">
            🎉 All caught up — no products waiting for a manual photo fix.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="admin-image-fixes-list">
            {items.map((p) => (
              <ImageFixRow key={p.id} p={p} onResolved={() => remove(p)} onReload={load} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ImageFixRow({ p, onResolved, onReload }) {
  const fileRef = React.useRef(null);
  const [name, setName] = useState(p.name || "");
  const [desc, setDesc] = useState(p.description || "");
  const [price, setPrice] = useState(String(p.price ?? ""));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        await api.post(`/admin/products/${p.id}/image`, { image_data: reader.result });
        toast.success("Image replaced — shopkeeper notified");
        onResolved?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    reader.onerror = () => { toast.error("Could not read file"); setUploading(false); };
    reader.readAsDataURL(f);
  };

  const saveDetails = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Title required"); return; }
    const priceNum = parseFloat(price);
    if (Number.isNaN(priceNum) || priceNum < 0) { toast.error("Invalid price"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/products/${p.id}`, { name: trimmed, description: desc, price: priceNum });
      toast.success("Details saved");
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async () => {
    if (!window.confirm(`Delete "${p.name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product removed");
      onResolved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const src = resolveImageSrc(p.image);

  return (
    <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/40" data-testid={`admin-image-fix-${p.id}`}>
      <div className="flex flex-wrap gap-4">
        <div className="flex-shrink-0">
          <a href={src} target="_blank" rel="noopener noreferrer" title="Open full photo">
            {src ? (
              <img src={src} alt={p.name}
                className="w-32 h-32 rounded-lg object-cover border border-amber-200"
                data-testid={`admin-image-fix-photo-${p.id}`} />
            ) : (
              <div className="w-32 h-32 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-3xl">📦</div>
            )}
          </a>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage}
            data-testid={`admin-image-fix-file-${p.id}`} />
          <Button size="sm" className="rounded-xl bg-[#0F3D2E] hover:bg-[#0F3D2E]/90 mt-2 w-full"
            data-testid={`admin-image-fix-upload-${p.id}`}
            onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : "📸 Replace photo"}
          </Button>
        </div>
        <div className="flex-1 min-w-[240px] space-y-2">
          <div className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{p.shop_name}</span>
            {p.shop_phone ? <> · 💬 +{p.shop_phone}</> : null}
            {p.shop_address ? <> · 📍 {p.shop_address}</> : null}
          </div>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Product title" maxLength={120}
            className="rounded-lg text-sm font-medium"
            data-testid={`admin-image-fix-name-${p.id}`} />
          <Input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min="0"
            placeholder="Price (₹)" className="rounded-lg text-sm w-32"
            data-testid={`admin-image-fix-price-${p.id}`} />
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
            placeholder="Description shoppers see" rows={3} maxLength={800}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-amber-400"
            data-testid={`admin-image-fix-desc-${p.id}`} />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] text-slate-400">
              Posted {p.created_at ? new Date(p.created_at).toLocaleString() : "—"} · ID {p.id.slice(0, 8)}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline"
                className="rounded-xl text-red-600 border-red-100 hover:bg-red-50"
                data-testid={`admin-image-fix-delete-${p.id}`}
                onClick={removeProduct}>Delete</Button>
              <Button size="sm" className="rounded-xl bg-amber-600 hover:bg-amber-700"
                data-testid={`admin-image-fix-save-${p.id}`}
                onClick={saveDetails} disabled={saving}>
                {saving ? "Saving…" : "Save details"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
